import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, Loader2, Check, Layers, RotateCcw } from "lucide-react";

/**
 * Sidebar Access modal. Drives:
 *   - super_admin granting nav keys to an agency's admin users (scope="agency")
 *   - admin / super_admin granting nav keys to an advertiser (scope="advertiser")
 *
 * The endpoint at /api/nav/permissions/{scope}/{id} returns:
 *   { available_keys, allowed_keys, uses_defaults, updated_at, ... }
 * `available_keys` is the set the caller is allowed to grant from (already cascaded server-side).
 */
export default function NavPermissionsModal({ scope, id, name, onClose }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [catalog, setCatalog] = useState([]);
    const [available, setAvailable] = useState([]);
    const [allowed, setAllowed] = useState([]);
    const [usesDefaults, setUsesDefaults] = useState(true);

    const endpoint = `/nav/permissions/${scope}/${id}`;
    const headerLabel = scope === "agency" ? "Admin sidebar access" : "Advertiser sidebar access";
    const subtitle = scope === "agency"
        ? "Tick the items the admins of this agency may see. Their advertisers can never see more than what you grant here."
        : "Tick the items this advertiser may see. Items the agency hasn't been granted are shown disabled.";

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            try {
                const [catResp, permResp] = await Promise.all([
                    api.get("/nav/catalog"),
                    api.get(endpoint),
                ]);
                if (cancelled) return;
                setCatalog(catResp.data?.items || []);
                setAvailable(permResp.data?.available_keys || []);
                setAllowed(permResp.data?.allowed_keys || []);
                setUsesDefaults(!!permResp.data?.uses_defaults);
            } catch (e) {
                toast.error(e.response?.data?.detail || "Failed to load permissions");
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => { cancelled = true; };
    }, [endpoint]);

    const toggle = (key) => {
        if (!available.includes(key)) return;
        setAllowed((p) => p.includes(key) ? p.filter((k) => k !== key) : [...p, key]);
    };
    const selectAll = () => setAllowed([...available]);
    const clearAll = () => setAllowed([]);
    const resetDefaults = () => setAllowed([...available]);

    const save = async () => {
        setSaving(true);
        try {
            await api.put(endpoint, { allowed_keys: allowed });
            toast.success(`Sidebar access updated for ${name}`);
            onClose?.(true);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    // Items that never make sense to grant to an advertiser — hide from the picker entirely
    // when scope === "advertiser" so admins don't even see them as an option.
    const HIDDEN_FROM_ADVERTISER = new Set(["agencies", "advertisers", "users"]);

    // Render the catalog so the order matches what the user sees in the actual sidebar.
    const rows = catalog
        .filter((c) => scope !== "advertiser" || !HIDDEN_FROM_ADVERTISER.has(c.key))
        .map((c) => ({
            ...c,
            isAvailable: available.includes(c.key),
            isAllowed: allowed.includes(c.key),
        }));

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/50" data-testid="nav-perm-modal">
            <div className="bg-white border border-zinc-200 rounded-lg shadow-xl w-full max-w-xl mx-4 max-h-[85vh] flex flex-col">
                {/* Header */}
                <div className="flex items-start justify-between p-5 border-b border-zinc-200">
                    <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-md wl-accent-bg flex items-center justify-center text-white">
                            <Layers className="h-5 w-5" strokeWidth={2.5} />
                        </div>
                        <div>
                            <div className="overline">{headerLabel}</div>
                            <h2 className="font-display text-lg text-zinc-900 truncate max-w-md" data-testid="nav-perm-title">{name}</h2>
                            <p className="text-xs text-zinc-500 mt-1 max-w-md">{subtitle}</p>
                        </div>
                    </div>
                    <button onClick={() => onClose?.(false)} className="p-1 rounded hover:bg-zinc-100 text-zinc-500" data-testid="nav-perm-close" aria-label="Close">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-5">
                    {loading ? (
                        <div className="flex items-center justify-center gap-2 text-zinc-500 py-12">
                            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-xs text-zinc-500">
                                    <span className="tabular-nums">{allowed.length}</span> / <span className="tabular-nums">{available.length}</span> selected
                                    {usesDefaults && <span className="ml-2 text-zinc-400">· using defaults</span>}
                                </div>
                                <div className="flex items-center gap-2 text-[11px]">
                                    <button onClick={selectAll} className="wl-accent-text hover:underline" data-testid="nav-perm-all">Select all</button>
                                    <span className="text-zinc-300">·</span>
                                    <button onClick={clearAll} className="text-zinc-500 hover:text-zinc-900" data-testid="nav-perm-clear">Clear</button>
                                    <span className="text-zinc-300">·</span>
                                    <button onClick={resetDefaults} className="inline-flex items-center gap-1 text-zinc-500 hover:text-zinc-900" data-testid="nav-perm-defaults"><RotateCcw className="h-3 w-3" />Defaults</button>
                                </div>
                            </div>
                            <div className="border border-zinc-200 rounded divide-y divide-zinc-100">
                                {rows.map((r) => {
                                    const disabled = !r.isAvailable;
                                    return (
                                        <label
                                            key={r.key}
                                            className={`flex items-center gap-3 px-3 py-2.5 text-sm ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:bg-zinc-50"}`}
                                            data-testid={`nav-perm-row-${r.key}`}
                                        >
                                            <span className={`h-4 w-4 rounded border flex items-center justify-center ${r.isAllowed ? "wl-accent-bg wl-accent-border" : "border-zinc-300 bg-white"}`}>
                                                {r.isAllowed && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                            </span>
                                            <input
                                                type="checkbox"
                                                className="sr-only"
                                                checked={r.isAllowed}
                                                onChange={() => toggle(r.key)}
                                                disabled={disabled}
                                            />
                                            <span className="text-zinc-900 flex-1">{r.label}</span>
                                            {disabled && (
                                                <span className="text-[10px] text-zinc-400 italic">
                                                    {scope === "agency"
                                                        ? "not in admin role"
                                                        : (r.default_roles && r.default_roles.includes("advertiser"))
                                                            ? "not granted to agency"
                                                            : "not available to advertisers"}
                                                </span>
                                            )}
                                        </label>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 p-4 border-t border-zinc-200 bg-zinc-50/50">
                    <button
                        onClick={() => onClose?.(false)}
                        className="px-3 py-2 rounded text-sm font-medium border border-zinc-300 hover:bg-zinc-100 text-zinc-700"
                        data-testid="nav-perm-cancel"
                    >Cancel</button>
                    <button
                        onClick={save}
                        disabled={loading || saving}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                        style={{ background: "var(--wl-primary-hex)", color: "#fff" }}
                        data-testid="nav-perm-save"
                    >
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}
